import { useEffect } from "react";
import { connect } from "react-redux";
import { type RouteComponentProps } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  listTraderRepresentatives,
  listTraders,
  updateTrader,
  updateTraderRepresentative,
  updateTraderRepresentativeReset,
  updateTraderReset,
  userDetail,
} from "../../actions";
import Loading from "../../components/Loading";
import { isEmpty } from "../../utils";
import { getUserLanguage } from "../../utils/i18n";
import { ProfileForm, type ProfileFormValues } from "./components/Form";
import type { CurrentUser, Trader, TraderRepresentative } from "./types";
import { User } from "lucide-react";

interface StateProps {
  currentUser: CurrentUser;
  hasUpdated: boolean;
  initialValues: Partial<ProfileFormValues>;
  isFetching: boolean;
  trader?: Trader;
  traderRepresentative?: TraderRepresentative;
}

interface DispatchProps {
  listTraders: (id: number | string) => void;
  listTraderRepresentatives: (id: number | string) => void;
  updateTrader: (id: number | string, data: Record<string, unknown>) => Promise<unknown>;
  updateTraderRepresentative: (
    id: number | string,
    data: Record<string, unknown>
  ) => Promise<unknown>;
  updateTraderReset: () => void;
  updateTraderRepresentativeReset: () => void;
  userDetail: () => void;
}

type ProfileProps = StateProps & DispatchProps & RouteComponentProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction) as unknown as Promise<unknown>;

// Representative-only page to edit trader + representative details.
export const Profile = ({
  currentUser,
  hasUpdated,
  history,
  initialValues,
  isFetching,
  listTraderRepresentatives,
  listTraders,
  trader,
  traderRepresentative,
  updateTrader,
  updateTraderRepresentative,
  updateTraderRepresentativeReset,
  updateTraderReset,
  userDetail,
}: ProfileProps) => {
  const { t } = useTranslation();

  const { representative, organization } = currentUser;
  const firstTimeLogin =
    (!isEmpty(currentUser) && !currentUser.terms_of_service_accepted_date) ||
    Boolean((history.location.state as { updateDetails?: boolean } | undefined)?.updateDetails);

  useEffect(() => {
    return () => {
      // Refresh current user (name may have changed) and clear update state on leave.
      userDetail();
      updateTraderRepresentativeReset();
      updateTraderReset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasUpdated) {
      history.push("/representative/offer", { fromProfileUpdate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUpdated]);

  useEffect(() => {
    if (representative) {
      listTraderRepresentatives(representative);
    }
    if (organization) {
      listTraders(organization);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [representative, organization]);

  const onValidate = (values: ProfileFormValues) => {
    if (!trader || !traderRepresentative) {
      return Promise.resolve();
    }
    const { name, physical_address, first_name, last_name, contact_phone, language } = values;
    return Promise.all([
      updateTrader(trader.id, { ...trader, name, physical_address }),
      updateTraderRepresentative(traderRepresentative.id, {
        ...traderRepresentative,
        language,
        organization: traderRepresentative.organization.id,
        person: { ...traderRepresentative.person, first_name, last_name, contact_phone },
      }),
    ]);
  };

  return (
    <main className="flex flex-col gap-6 px-6 py-4 sm:px-8 sm:py-6 md:px-10 md:py-8">
      <div className="flex items-center gap-x-2">
        <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-blue-600 bg-blue-100">
          <User />
        </span>
        <h1 className="font-semibold text-2xl">{t("profile.title")}</h1>
      </div>
      {isFetching || !trader || !traderRepresentative ? (
        <Loading isVisible />
      ) : (
        <div className="mx-auto w-full max-w-2xl space-y-6">
          {firstTimeLogin && (
            <Alert variant="info">
              <AlertDescription>{t("profile.welcome")}</AlertDescription>
            </Alert>
          )}
          <ProfileForm trader={trader} initialValues={initialValues} onValidate={onValidate} />
        </div>
      )}
    </main>
  );
};

interface RootState {
  form?: unknown;
  listTraders: { data: { results: Trader[] }; isFetching: boolean };
  listTraderRepresentatives: { data: { results: TraderRepresentative[] }; isFetching: boolean };
  updateTrader: { data: { id?: number | string } };
  updateTraderRepresentative: { data: { id?: number | string } };
  userDetail: { data: CurrentUser; isFetching: boolean };
}

export const mapStateToProps = (state: RootState): StateProps => {
  const trader = state.listTraders.data.results.find(
    ({ id }) => id === state.userDetail.data.organization
  );
  const traderRepresentative = state.listTraderRepresentatives.data.results.find(
    ({ id }) => id === state.userDetail.data.representative
  );
  const initialValues: Partial<ProfileFormValues> =
    trader && traderRepresentative
      ? {
          name: trader.name,
          physical_address: trader.physical_address,
          first_name: traderRepresentative.person.first_name,
          last_name: traderRepresentative.person.last_name,
          contact_phone: traderRepresentative.person.contact_phone,
          language: getUserLanguage(),
        }
      : {};

  return {
    currentUser: state.userDetail.data,
    hasUpdated:
      Boolean(state.updateTrader.data.id) && Boolean(state.updateTraderRepresentative.data.id),
    initialValues,
    isFetching:
      state.listTraderRepresentatives.isFetching ||
      state.listTraders.isFetching ||
      state.userDetail.isFetching,
    trader,
    traderRepresentative,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listTraders: id => dispatchAction(dispatch, listTraders({ id })),
  listTraderRepresentatives: id => dispatchAction(dispatch, listTraderRepresentatives({ id })),
  updateTrader: (id, data) => dispatchAction(dispatch, updateTrader(id, data)),
  updateTraderRepresentative: (id, data) =>
    dispatchAction(dispatch, updateTraderRepresentative(id, data)),
  updateTraderReset: () => dispatchAction(dispatch, updateTraderReset()),
  updateTraderRepresentativeReset: () =>
    dispatchAction(dispatch, updateTraderRepresentativeReset()),
  userDetail: () => dispatchAction(dispatch, userDetail("", "", true)),
});

export const ConnectedProfile = connect(mapStateToProps, mapDispatchToProps)(Profile);
